// Add immediate console log to verify script execution

import { captureException, initSentry } from './services/sentry';

initSentry();

// Add global error handlers FIRST to catch initialization errors
window.addEventListener('error', (e) => {
  captureException(e.error || e.message, {
    source: 'window.error',
    filename: e.filename,
    lineno: e.lineno,
    colno: e.colno,
  });

  // Only log in development mode to avoid Lighthouse console error penalties
  if (import.meta.env.DEV) {
    console.error('Global error:', e);
  }
  // Surface the stack/module causing first paint failure
});
window.addEventListener('unhandledrejection', (e) => {
  captureException(e.reason, { source: 'window.unhandledrejection' });

  // Only log in development mode to avoid Lighthouse console error penalties  
  if (import.meta.env.DEV) {
    console.error('Unhandled rejection:', e);
  }
});

// Import polyfills FIRST
import './utils/capacitorPolyfill'; // Must be first to patch history API
// import './utils/base64-polyfill'; // Now loaded dynamically when PDF features are used

// Import React and ReactDOM BEFORE any other React components
import React from 'react';
import ReactDOM from 'react-dom/client';
import { HelmetProvider } from 'react-helmet-async';

// Import both apps
import AppDefault from './App';
import IOSApp from './IOSApp';

// For iOS compatibility, use IOSApp when running in Capacitor iOS
// Check if we're in Capacitor iOS environment
const isIOSCapacitor = typeof window !== 'undefined' && 
  (window as any).Capacitor?.isNativePlatform?.() && 
  (window as any).Capacitor?.getPlatform?.() === 'ios';

// Choose which app to use based on platform
const App = isIOSCapacitor ? IOSApp : AppDefault;

if (isIOSCapacitor) {
  document.documentElement.classList.add('capacitor-ios');
} else {
  document.documentElement.classList.remove('capacitor-ios');
}

import './utils/websocketErrorHandler';
import { initExtensionErrorFilter } from './utils/browserExtensionHandler';
import { registerServiceWorker } from './utils/serviceWorker';

// Initialize browser extension error filter to suppress irrelevant console errors
initExtensionErrorFilter();

// Register service worker for better caching and chunk loading error handling
registerServiceWorker();

/**
 * Application Entry Point
 * 
 * PROJECT_RULES.md Compliance:
 * - ✅ Minimal bundle impact: Entry point only
 * - ✅ TypeScript: Proper typing for React 18
 * - ✅ Performance: Uses React 18's createRoot for concurrent features
 * - ✅ Security: No exposed configuration or secrets
 * - ✅ Error handling: Graceful fallback for critical failures
 * 
 * Follows Phase 1 scope - just the basics to get the app running
 */

// Simple error boundary for critical failures
const CriticalErrorFallback: React.FC<{ error: Error }> = ({ error }) => (
  <div style={{
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '20px',
    backgroundColor: '#f9fafb',
    fontFamily: '-apple-system, BlinkMacSystemFont, Segoe UI, sans-serif'
  }}>
    <div style={{
      backgroundColor: 'white',
      padding: '40px',
      borderRadius: '8px',
      boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
      textAlign: 'center',
      maxWidth: '500px'
    }}>
      <h1 style={{ color: '#ef4444', marginBottom: '16px' }}>DinnerBrain - Loading Error</h1>
      <p style={{ color: '#6b7280', marginBottom: '24px' }}>
        There was an error loading the application. This might be a temporary issue.
      </p>
      <button
        onClick={() => window.location.reload()}
        style={{
          backgroundColor: '#4A6651',
          color: 'white',
          border: 'none',
          padding: '12px 24px',
          borderRadius: '6px',
          cursor: 'pointer',
          fontSize: '16px',
          minWidth: '44px',
          minHeight: '44px'
        }}
      >
        Reload Page
      </button>
      <details style={{ marginTop: '24px', textAlign: 'left' }}>
        <summary style={{ cursor: 'pointer', color: '#6b7280' }}>Error Details</summary>
        <pre style={{ 
          backgroundColor: '#f3f4f6', 
          padding: '12px', 
          borderRadius: '4px', 
          fontSize: '12px',
          overflow: 'auto',
          marginTop: '8px'
        }}>
          {error.message}
          {error.stack && '\n\n' + error.stack}
        </pre>
      </details>
    </div>
  </div>
);

// Ensure root element exists with proper typing
const rootElement = document.getElementById('root');
if (!rootElement) {
  // If no root element, create fallback content directly
  document.body.innerHTML = `
    <div style="min-height: 100vh; display: flex; align-items: center; justify-content: center; padding: 20px; background-color: #f9fafb; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;">
      <div style="background-color: white; padding: 40px; border-radius: 8px; box-shadow: 0 1px 3px rgba(0,0,0,0.1); text-align: center;">
        <h1 style="color: #ef4444; margin-bottom: 16px;">DinnerBrain - Setup Error</h1>
        <p style="color: #6b7280;">Could not find root element. Please check your HTML setup.</p>
      </div>
    </div>
  `;
  throw new Error('Failed to find the root element');
}

// Error boundary component
class ErrorBoundary extends React.Component<
  { children: React.ReactNode },
  { hasError: boolean; error: Error | null }
> {
  constructor(props: { children: React.ReactNode }) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    captureException(error, {
      source: 'critical-error-boundary',
      componentStack: errorInfo.componentStack,
    });
  }

  render() {
    if (this.state.hasError) {
      return <CriticalErrorFallback error={this.state.error || new Error('Unknown error')} />;
    }
    return this.props.children;
  }
}

// Create root using React 18's new API for better performance
const root = ReactDOM.createRoot(rootElement);

// Add debug logging

// Add immediate visual feedback
rootElement.innerHTML = '<div style="padding: 20px; text-align: center;">Initializing DinnerBrain...</div>';

// Render app with error boundary
try {
  root.render(
    <ErrorBoundary>
      <HelmetProvider>
        <App />
      </HelmetProvider>
    </ErrorBoundary>
  );
} catch (error) {
  rootElement.innerHTML = `<div style="padding: 20px; color: red;">Failed to render: ${error}</div>`;
}